import { memo } from 'react'
import { Handle, Position, type Node } from '@xyflow/react'
import { UserCheck, X, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { HumanReviewNodeConfig, ReviewAction } from './HumanReviewNode'

export type HumanReviewNodeStatus = 'idle' | 'configured' | 'waiting' | 'completed' | 'timeout' | 'error'

export interface HumanReviewCanvasNodeData extends HumanReviewNodeConfig {
  status?: HumanReviewNodeStatus
  errorMessage?: string
  assigneeName?: string
  onDelete?: () => void
  [key: string]: unknown
}

export type HumanReviewCanvasNode = Node<HumanReviewCanvasNodeData, 'humanReview'>

const actionColors: Record<ReviewAction, string> = {
  approve: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  reject: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  request_changes: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  custom: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
}

const actionLabels: Record<ReviewAction, string> = {
  approve: 'Approve',
  reject: 'Reject',
  request_changes: 'Changes',
  custom: 'Custom',
}

interface HumanReviewCanvasNodeProps {
  data: HumanReviewCanvasNodeData
  selected?: boolean
}

function HumanReviewCanvasNodeComponent({ data, selected }: HumanReviewCanvasNodeProps) {
  return (
    <div
      className={cn(
        'group relative bg-white dark:bg-gray-800 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 min-w-[200px] max-w-[240px] overflow-hidden',
        'border border-gray-200 dark:border-gray-700',
        selected && 'ring-2 ring-cyan-500 ring-offset-2 dark:ring-offset-gray-900 border-cyan-500'
      )}
    >
      {/* Left color accent bar */}
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-cyan-500" />

      {/* Input Handle */}
      <Handle
        type="target"
        position={Position.Top}
        className="!w-2.5 !h-2.5 !bg-emerald-500 !border-2 !border-white dark:!border-gray-800 !-top-1.5"
      />

      {/* Delete button */}
      <button
        onClick={(e) => {
          e.stopPropagation()
          data.onDelete?.()
        }}
        className="absolute top-2 right-2 p-1 rounded-md opacity-0 group-hover:opacity-100 hover:bg-red-50 dark:hover:bg-red-900/30 text-gray-400 hover:text-red-500 transition-all"
      >
        <X className="h-3.5 w-3.5" />
      </button>

      {/* Node Content */}
      <div className="pl-4 pr-3 py-3">
        <div className="flex items-start gap-2.5">
          <div className="flex-shrink-0 mt-0.5">
            <div className="p-1.5 rounded-lg bg-cyan-100 dark:bg-cyan-900/40">
              <UserCheck className="h-4 w-4 text-cyan-600 dark:text-cyan-400" />
            </div>
          </div>
          <div className="min-w-0 flex-1 pr-4">
            <p className="font-medium text-gray-900 dark:text-white text-sm leading-tight">
              {data.reviewTitle || (data.label as string) || 'Human Review'}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
              {data.assigneeName || (data.assigneeType ? 'Reviewer assigned' : 'Waiting for review')}
            </p>
          </div>
        </div>

        {/* Waiting indicator */}
        {data.status === 'waiting' && (
          <div className="mt-2 p-2 rounded-lg bg-amber-50 dark:bg-amber-900/20">
            <div className="flex items-center gap-2">
              <Loader2 className="h-3 w-3 text-amber-500 animate-spin" />
              <p className="text-xs text-amber-600 dark:text-amber-400">Waiting for review...</p>
            </div>
          </div>
        )}

        {/* Error message */}
        {data.status === 'error' && data.errorMessage && (
          <div className="mt-2 p-2 rounded-lg bg-red-50 dark:bg-red-900/20">
            <p className="text-xs text-red-600 dark:text-red-400 line-clamp-2">{data.errorMessage}</p>
          </div>
        )}

        {/* Allowed actions badges */}
        {data.allowedActions && data.allowedActions.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {data.allowedActions.slice(0, 3).map((action: ReviewAction) => (
              <span
                key={action}
                className={cn('inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium', actionColors[action])}
              >
                {actionLabels[action]}
              </span>
            ))}
            {data.allowedActions.length > 3 && (
              <span className="text-[10px] text-gray-400">+{data.allowedActions.length - 3}</span>
            )}
          </div>
        )}
      </div>

      {/* Output Handle */}
      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-2.5 !h-2.5 !bg-emerald-500 !border-2 !border-white dark:!border-gray-800 !-bottom-1.5"
      />
    </div>
  )
}

export default memo(HumanReviewCanvasNodeComponent)
